import { useNavigate } from "core/hooks/useNavigate";
import { useToaster } from "core/hooks/useToaster";
import { useAddUserLog } from "core/hooks/useUserLog";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import {
  copyTranslationsToClipboard,
  getSerializedMissingTranslations,
} from "i18n.config";
import { useRouter } from "next/router";
import { useQueryState } from "nuqs";
import { useAuth } from "oidc-react";
import { type MouseEvent, useCallback, useState } from "react";
import type { DevMenuOption, MenuOption } from "./shared";

export function useUserMenu() {
  const { successToast, errorToast } = useToaster();
  const addLog = useAddUserLog();
  const { push } = useRouter();
  const navigate = useNavigate();
  const { signOutRedirect } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleOpenUserMenu = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);
  const { 1: setCustomizeTheme } = useQueryState("customizeTheme");
  const handleCloseUserMenu = useCallback(
    async (option: MenuOption | DevMenuOption | null) => {
      if (!option) {
        setAnchorEl(null);
        return;
      }
      switch (option) {
        case "logout":
          await addLog("Page", "logout");
          await signOutRedirect();
          break;
        case "region-select":
          push("/region-select");
          break;
        case "user-preferences":
          await navigate("/user-preferences");
          break;
        case "dms-debug":
          await navigate("/dms/permission-diagnostic");
          break;
        case "impersonate":
          await navigate("/manage/impersonate");
          break;
        case "logo-debug":
          await navigate("/brand-logo-debug");
          break;
        case "copy-translations":
          try {
            copyTranslationsToClipboard();
            successToast("Translations copied to clipboard");
          } catch {
            errorToast(
              "Translations failed to copy to clipboard, are you browsing from a secure origin?"
            );
          }
          break;
        case "populate-localizations": {
          const translations = getSerializedMissingTranslations();
          if (!translations) {
            errorToast("No missing translations to populate");
            break;
          }
          try {
            const response = await fetch("/api/populate-localizations", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(translations),
            });
            const data = await response.json();
            if (response.ok) {
              successToast(data.results.join("\n"));
            } else {
              errorToast(data.error);
            }
          } catch {
            errorToast("Failed to populate localizations");
          }
          break;
        }
        case "test-page":
          await navigate("/test-page");
          break;
        case "localization-inspect-page":
          await navigate("/localization-inspect");
          break;
        case "theme-inspect-page":
          await navigate("/theme-inspect");
          break;
        case "app-config-info":
          await navigate("/app-config-info");
          break;
        case "customize-theme":
          setCustomizeTheme("true");
          break;
        default:
          exhaustiveCheck(option);
          break;
      }
      setAnchorEl(null);
    },
    [
      addLog,
      errorToast,
      navigate,
      push,
      setCustomizeTheme,
      signOutRedirect,
      successToast,
    ]
  );
  return { anchorEl, handleOpenUserMenu, handleCloseUserMenu, setAnchorEl };
}
